import { unwrapData } from './chunk-HIWBYL4O.js';

// src/plans/usageApi.ts
function asArray(raw) {
  return Array.isArray(raw) ? raw : [];
}
function asRecord(raw) {
  return raw && typeof raw === "object" && !Array.isArray(raw) ? raw : {};
}
function asEpoch(raw) {
  const n = typeof raw === "string" ? Number(raw) : raw;
  return typeof n === "number" && Number.isFinite(n) ? n : null;
}
function asNumber(raw) {
  const n = typeof raw === "string" && raw.trim() !== "" ? Number(raw) : raw;
  return typeof n === "number" && Number.isFinite(n) ? n : null;
}
function eventFromWire(raw) {
  const wire = asRecord(raw);
  const quantity = wire.quantity == null ? 1 : asNumber(wire.quantity);
  return { ...wire, quantity: quantity ?? 0 };
}
function numberRecord(raw) {
  const out = {};
  for (const [key, value] of Object.entries(asRecord(raw))) {
    const n = asNumber(value);
    if (n != null) out[key] = n;
  }
  return out;
}
function rollupFromWire(raw, events) {
  const wire = asRecord(raw);
  const billable = asNumber(wire.billable);
  if (billable == null) return rollupUsage(events);
  return {
    billable,
    telemetry: asNumber(wire.telemetry) ?? 0,
    credits: asNumber(wire.credits) ?? 0,
    events: asNumber(wire.events) ?? events.length,
    // Absent means "not split here" (counter totals), never zero per kind.
    byKind: wire.byKind && typeof wire.byKind === "object" ? numberRecord(wire.byKind) : null
  };
}
function meteringFromWire(raw) {
  const wire = asRecord(raw);
  if (typeof wire.available !== "boolean") return { available: true, reason: null };
  return {
    available: wire.available,
    reason: typeof wire.reason === "string" ? wire.reason : null,
    truncated: wire.truncated === true,
    ...typeof wire.events_complete === "boolean" ? { eventsComplete: wire.events_complete } : {}
  };
}
function periodFromWire(raw) {
  const wire = asRecord(raw);
  return {
    start: asEpoch(wire.start),
    end: asEpoch(wire.end),
    granularity: typeof wire.granularity === "string" ? wire.granularity : "custom"
  };
}
function groupsFromWire(raw) {
  if (!Array.isArray(raw)) return null;
  return raw.map((row) => {
    const g = asRecord(row);
    return {
      key: typeof g.key === "string" ? g.key : "unattributed",
      rollup: rollupFromWire(g.rollup, [])
    };
  });
}
function formatUsagePeriod(period) {
  if (!period) return "";
  if (period.granularity === "month" && period.start != null) {
    const start = new Date(period.start * 1e3);
    return `${start.toLocaleDateString(void 0, { month: "long", year: "numeric" })} to date`;
  }
  if (period.granularity === "month") return "This month to date";
  const fmt = (epoch) => epoch == null ? "\u2026" : new Date(epoch * 1e3).toLocaleDateString();
  if (period.start == null && period.end == null) return "All time";
  return `${fmt(period.start)} \u2014 ${fmt(period.end)}`;
}
function createUsageApi(client, options = {}) {
  const basePath = options.basePath ?? "/usage";
  return {
    async getUsage(query = {}) {
      const res = await client.get(basePath, {
        query: {
          start: query.start ?? null,
          end: query.end ?? null,
          pipeline_run_id: query.pipelineRunId ?? null,
          tx_id: query.txId ?? null,
          group_by: query.groupBy ?? null,
          customer_id: query.customerId ?? null,
          ...query.executions ? { executions: JSON.stringify(query.executions) } : {}
        }
      });
      const body = asRecord(unwrapData(res));
      const events = asArray(body.events).map(eventFromWire);
      return {
        events,
        rollup: rollupFromWire(body.rollup, events),
        groups: groupsFromWire(body.groups),
        period: periodFromWire(body.period),
        attribution: asRecord(body.attribution),
        metering: meteringFromWire(body.metering)
      };
    }
  };
}
async function readExecutionUsage(api, executions) {
  const unique = [...new Map(executions.map((e) => [JSON.stringify(e), e])).values()];
  const reports = [];
  let next = 0;
  await Promise.all(Array.from({ length: Math.min(2, Math.ceil(unique.length / 20)) }, async () => {
    while (next < unique.length) {
      const chunk = unique.slice(next, next += 20);
      const report = await api.getUsage({ executions: chunk });
      if (report.metering.available && !report.metering.reason && report.period.granularity !== "executions") {
        throw new Error("This deployment does not support execution-scoped usage yet");
      }
      reports.push(report);
    }
  }));
  const rollup = rollupUsage([]);
  let byKind = {};
  for (const report of reports) {
    for (const key of ["billable", "telemetry", "credits", "events"]) {
      rollup[key] += report.rollup[key];
    }
    if (!report.rollup.byKind) byKind = null;
    else if (byKind) {
      for (const [kind, count] of Object.entries(report.rollup.byKind)) {
        byKind[kind] = (byKind[kind] ?? 0) + count;
      }
    }
  }
  rollup.byKind = byKind;
  const unavailable = reports.find((r) => !r.metering.available || r.metering.reason === "no_customer");
  const merged = reports.flatMap((r) => r.events).sort((a, b) => Date.parse(b.occurred_at) - Date.parse(a.occurred_at));
  const events = merged.slice(0, 1e3);
  return {
    events,
    rollup,
    groups: null,
    period: { start: null, end: null, granularity: "executions" },
    attribution: reports[0]?.attribution ?? {},
    metering: {
      available: unavailable?.metering.available ?? true,
      reason: unavailable?.metering.reason ?? null,
      truncated: reports.some((r) => r.metering.truncated),
      eventsComplete: events.length === merged.length && reports.every((r) => r.metering.eventsComplete !== false)
    }
  };
}

// src/plans/index.ts
var UsageEventKind = {
  PER_TX: "per_tx",
  /** One completed tracer run (an N-hop value-flow walk). Billable. */
  PER_TRACE: "per_trace",
  /** One question the Ask AI assistant answered (tokens in metadata). Billable. */
  PER_AI_TURN: "per_ai_turn",
  PER_ENRICHMENT: "per_enrichment",
  PER_STEP: "per_step",
  PER_PROVIDER_CALL: "per_provider_call",
  CREDIT: "credit"
};
var USAGE_UNIT_TERMS = {
  [UsageEventKind.PER_TX]: { billable: true, perTier: false },
  [UsageEventKind.PER_TRACE]: { billable: true, perTier: false },
  [UsageEventKind.PER_AI_TURN]: { billable: true, perTier: false },
  [UsageEventKind.PER_PROVIDER_CALL]: { billable: false, perTier: true },
  [UsageEventKind.PER_STEP]: { billable: false, perTier: true },
  [UsageEventKind.PER_ENRICHMENT]: { billable: false, perTier: true }
};
var USAGE_KIND_LABELS = {
  [UsageEventKind.PER_TX]: { one: "Transaction", many: "Transactions" },
  [UsageEventKind.PER_TRACE]: { one: "Tracer run", many: "Tracer runs" },
  [UsageEventKind.PER_AI_TURN]: { one: "Assistant answer", many: "Assistant answers" },
  [UsageEventKind.PER_PROVIDER_CALL]: { one: "Provider call", many: "Provider calls" },
  [UsageEventKind.PER_STEP]: { one: "Step", many: "Steps" },
  [UsageEventKind.PER_ENRICHMENT]: { one: "Enrichment", many: "Enrichments" },
  [UsageEventKind.CREDIT]: { one: "Credit", many: "Credits" }
};
function usageKindLabel(kind, count = "many") {
  const label = USAGE_KIND_LABELS[kind];
  return label ? label[count] : kind;
}
var BILLABLE_USAGE_KINDS = Object.keys(USAGE_UNIT_TERMS).filter((kind) => USAGE_UNIT_TERMS[kind].billable);
function isBillableKind(kind) {
  return kind !== UsageEventKind.CREDIT && USAGE_UNIT_TERMS[kind]?.billable === true;
}
var UsageUnit = {
  COUNT: "count",
  BYTES: "bytes",
  SECONDS: "seconds"
};
var UsageMetadataKey = {
  /** Whether this row should reach an invoice, decided once at record time. */
  BILLABLE: "billable",
  /** Ladder rung the unit's quota window counts at. */
  BILLING_SCOPE: "billing_scope",
  /** Which id format produced the row — `"v1"` before the cutover, `"v2"` after. */
  ATTRIBUTION_VERSION: "attribution_version"
};
function isCreditEvent(e) {
  return e.kind === UsageEventKind.CREDIT;
}
var EnforcementMode = { SOFT: "soft", HARD: "hard" };
var OveragePolicy = { ALLOW: "allow", THROTTLE: "throttle", REJECT: "reject" };
var QuotaStatus = { ALLOW: "allow", WARN: "warn", THROTTLE: "throttle", REJECT: "reject" };
function isBoundedWindow(w) {
  return w.granularity !== "all";
}
function policyMatches(policy, event) {
  if (event.kind !== policy.kind) return false;
  if (policy.subkind != null && event.subkind !== policy.subkind) return false;
  if (policy.applies_to_tiers && policy.applies_to_tiers.length > 0) {
    if (!policy.applies_to_tiers.includes(event.tier ?? "")) return false;
  }
  return true;
}
function isBlocking(d) {
  return d.status === QuotaStatus.REJECT || d.status === QuotaStatus.THROTTLE;
}
function isReject(d) {
  return d.status === QuotaStatus.REJECT;
}
function quotaUsedFraction(used, limit) {
  if (!limit || limit <= 0) return 0;
  return used / limit;
}
function evaluateQuota(policy, used) {
  const limit = policy.limit;
  const headroom = limit > 0 ? Math.max(0, limit - used) : Number.POSITIVE_INFINITY;
  const fraction = quotaUsedFraction(used, limit);
  let status = QuotaStatus.ALLOW;
  if (limit > 0 && used >= limit) {
    status = policy.enforcement === EnforcementMode.SOFT || policy.overage === OveragePolicy.ALLOW ? QuotaStatus.WARN : policy.overage === OveragePolicy.THROTTLE ? QuotaStatus.THROTTLE : QuotaStatus.REJECT;
  } else if (policy.soft_limit != null && used >= policy.soft_limit) {
    status = QuotaStatus.WARN;
  }
  return { status, headroom, fraction };
}
function hasEntitlement(plan, name) {
  return plan.entitlements.some((e) => e.name === name && e.enabled);
}
function getEntitlement(plan, name) {
  return plan.entitlements.find((e) => e.name === name) ?? null;
}
var ent = (name, enabled = true) => ({ name, enabled });
var win = (granularity) => ({ granularity });
var PLAN_FREE = {
  id: "free",
  display_name: "Free",
  description: "Explore transactions and addresses, organize investigations, and use basic annotations.",
  entitlements: [
    ent("catalog_lookup"),
    ent("address_catalog"),
    ent("tx_catalog"),
    ent("auto_tagging"),
    ent("risk_scoring", false),
    ent("price_enrichment", false),
    // Automation and workspace configuration start on Pro.
    ent("triggers", false),
    ent("webhook_callbacks", false),
    ent("graphql_fast_path"),
    ent("graphql_writes"),
    ent("graphql_per_operation", false),
    ent("tenancy_switching", false),
    ent("workspace_defaults", false),
    ent("tenancy_creation", false),
    ent("custom_roles", false),
    ent("realtime_streaming"),
    ent("catalog_advanced", false),
    ent("primary_chains", false),
    ent("catalog_confidence_controls", false),
    ent("actors", false),
    ent("situations", false),
    ent("custom_pipelines", false),
    ent("custom_grouping_rules", false),
    ent("custom_integrations", false),
    ent("api_keys", false)
  ],
  quotas: [
    { id: "free_monthly_tx", kind: UsageEventKind.PER_TX, window: win("month"), limit: 1e4, soft_limit: 9e3, enforcement: EnforcementMode.HARD, overage: OveragePolicy.REJECT },
    { id: "free_minute_burst", kind: UsageEventKind.PER_TX, window: win("minute"), limit: 60, enforcement: EnforcementMode.HARD, overage: OveragePolicy.REJECT }
  ]
};
var PLAN_PRO = {
  id: "pro",
  display_name: "Pro",
  description: "Paid tier with higher caps and full enrichment surface.",
  entitlements: [
    ent("catalog_lookup"),
    ent("address_catalog"),
    ent("tx_catalog"),
    ent("auto_tagging"),
    ent("risk_scoring"),
    ent("price_enrichment"),
    ent("triggers"),
    ent("webhook_callbacks"),
    ent("graphql_fast_path"),
    ent("graphql_writes"),
    ent("graphql_per_operation"),
    ent("tenancy_switching"),
    ent("workspace_defaults"),
    ent("tenancy_creation"),
    ent("custom_roles"),
    ent("realtime_streaming"),
    ent("catalog_advanced"),
    ent("primary_chains"),
    ent("catalog_confidence_controls"),
    ent("actors"),
    ent("situations"),
    ent("custom_pipelines"),
    ent("custom_grouping_rules"),
    ent("custom_integrations"),
    ent("api_keys")
  ],
  quotas: [
    { id: "pro_monthly_tx", kind: UsageEventKind.PER_TX, window: win("month"), limit: 1e6, soft_limit: 9e5, enforcement: EnforcementMode.HARD, overage: OveragePolicy.THROTTLE },
    { id: "pro_minute_burst", kind: UsageEventKind.PER_TX, window: win("minute"), limit: 600, enforcement: EnforcementMode.HARD, overage: OveragePolicy.THROTTLE }
  ]
};
var PLAN_ENTERPRISE = {
  id: "enterprise",
  display_name: "Enterprise",
  description: "Custom-quota tier with soft enforcement (audit only).",
  entitlements: [
    ent("catalog_lookup"),
    ent("address_catalog"),
    ent("tx_catalog"),
    ent("auto_tagging"),
    ent("risk_scoring"),
    ent("price_enrichment"),
    ent("triggers"),
    ent("webhook_callbacks"),
    ent("graphql_fast_path"),
    ent("graphql_writes"),
    ent("graphql_per_operation"),
    ent("tenancy_switching"),
    ent("workspace_defaults"),
    ent("tenancy_creation"),
    ent("custom_roles"),
    ent("realtime_streaming"),
    ent("catalog_advanced"),
    ent("primary_chains"),
    ent("catalog_confidence_controls"),
    ent("actors"),
    ent("situations"),
    ent("custom_pipelines"),
    ent("custom_grouping_rules"),
    ent("custom_integrations"),
    ent("api_keys"),
    ent("sla_99_99")
  ],
  quotas: [
    { id: "enterprise_monthly_tx", kind: UsageEventKind.PER_TX, window: win("month"), limit: 0, enforcement: EnforcementMode.SOFT, overage: OveragePolicy.ALLOW }
  ]
};
function bundledPlans() {
  return [PLAN_FREE, PLAN_PRO, PLAN_ENTERPRISE];
}
var ENTITLEMENT = {
  /** Actor rankings and conduct, including their tenancy defaults. */
  ACTORS: "actors",
  SITUATIONS: "situations",
  TRIGGERS: "triggers",
  /** Reads may use the composed GraphQL path instead of REST. */
  GRAPHQL_FAST_PATH: "graphql_fast_path",
  /** Writes may go over GraphQL. */
  GRAPHQL_WRITES: "graphql_writes",
  /** The per-operation transport overrides are offered at all. */
  GRAPHQL_PER_OPERATION: "graphql_per_operation",
  /** Multi-tenant: the tenant switcher, and the tenancy admin surface. */
  TENANCY_SWITCHING: "tenancy_switching",
  /**
   * Creating ADDITIONAL organizations and tenants — a customer's first of each
   * comes with signup. Meant to be withheld on Free, where the create controls
   * are hidden rather than upsold; granted on every tier until packaging is
   * switched on (set `false` here AND in `plans.py`). The API refuses with
   * `PERM003` via `entitlement:tenancy_creation` on management-api's routes.
   */
  TENANCY_CREATION: "tenancy_creation",
  /**
   * Server real-time streaming: the AppSync socket that pushes transactions,
   * address watches, catalog, groups, notifications and collaboration. Without
   * it the dashboard opens no server connection and hides every live control;
   * cross-tab messaging inside the browser keeps working on every tier. The
   * AppSync subscribe / publishToChannel templates refuse it server-side.
   */
  REALTIME_STREAMING: "realtime_streaming",
  /**
   * Advanced catalog authoring: per-chain overrides and chain applicability,
   * headline-address primaries, entity membership, attaching to entities/events,
   * team/user sharing, verified status, bulk writes and review decisions.
   * Without it the advanced controls are hidden (basic annotation stays). Meant
   * for Enterprise; enforced server-side by commons.authz.catalog_authoring.
   */
  CATALOG_ADVANCED: "catalog_advanced",
  /**
   * Choosing PRIMARY CHAINS: an entity's (tag's, label's, group's)
   * `primary_chains` and an address's `is_primary_chain`. Its own switch, not
   * part of `catalog_advanced`, so it can be withheld on its own. Without it the
   * choosing controls are hidden; primaries already set — including the ones
   * system defaults set — keep showing everywhere they are read. Meant for
   * Enterprise, granted on every tier today: switching it off a tier is setting
   * it `false` here AND in `plans.py`. Enforced by `commons.authz.primary_chains`.
   */
  PRIMARY_CHAINS: "primary_chains",
  CATALOG_CONFIDENCE_CONTROLS: "catalog_confidence_controls",
  /** An admin may publish defaults that everyone in the tenant inherits. */
  WORKSPACE_DEFAULTS: "workspace_defaults",
  /**
   * Creating, editing and granting CUSTOM roles — team roles that are not
   * `system`, and org roles with a `rol_…` id. Built-in roles are every tier's.
   *
   * "Keep, but freeze": without it a customer keeps the custom roles they have
   * (and the people holding them keep them), may still delete one or take one
   * away, but cannot create, edit or hand one to anybody new. Enforced by
   * `commons.authz.custom_roles` (a `PERM003` refusal); the UI only withholds.
   *
   * Intended for Enterprise, granted on every tier today. Switching packaging
   * on is setting it `false` on free and pro here AND in `plans.py`.
   */
  CUSTOM_ROLES: "custom_roles",
  /**
   * Creating and editing grouping rules (`/grouping-rules`) — telling the engine
   * how to correlate transactions beyond its bundled recipes, which are every
   * tier's.
   *
   * "Keep, but freeze", as custom roles: without it a customer's existing rules
   * keep grouping and may still be disabled or deleted; creating one and
   * changing one stop. Enforced by `commons.authz.grouping_rules` (a `PERM003`
   * refusal); the UI only withholds.
   *
   * Meant for Enterprise, granted on every tier today. Switching packaging on is
   * setting it `false` on free and pro here AND in `plans.py`.
   */
  CUSTOM_GROUPING_RULES: "custom_grouping_rules",
  /**
   * Authoring pipelines — creating one, editing one, validating or dry-running a
   * draft. Listing, reading and running are every tier's.
   *
   * Unlike the two above this one WITHDRAWS a scope rather than freezing a
   * surface: it gates `pipelines:write` in {@link PLAN_GATED_SCOPES}, so a tier
   * without it produces a session whose token never carried the verb. That is
   * why the name is here at all — Settings › Access reads it to explain the
   * absence.
   */
  CUSTOM_PIPELINES: "custom_pipelines",
  /**
   * Authoring custom INTEGRATIONS — uploading code, building it and publishing
   * a version an integration action or a pipeline `custom_step` can invoke.
   * Using one somebody already published is every tier's.
   *
   * How MANY a customer may keep is not this: that is the `integration_limit`
   * plan setting (a count, `0` = unlimited), served with the plan. A cap is a
   * different refusal from "not in your plan" — `PERM004` rather than
   * `PERM003` — so the UI can say which. Enforced by
   * `commons.authz.integration_access`; the UI only withholds.
   *
   * Granted on every tier today. Switching packaging on is setting it `false`
   * here AND in `plans.py`.
   */
  CUSTOM_INTEGRATIONS: "custom_integrations",
  /**
   * Minting API KEYS — `ake_` credentials that call the platform as the person
   * who made them, carrying a subset of their own scopes. USING one is not
   * gated: the authorizer resolves the stored record long before any
   * subscription is read, so a plan change never breaks a customer's running
   * integrations. This is whether they may create more.
   *
   * How MANY is not this: that is the `api_key_limit` plan setting (a count,
   * `0` = unlimited), served with the plan, and refused `PERM004` rather than
   * `PERM003` so the UI can say which. Enforced by
   * `commons.authz.api_key_access`; the UI only withholds.
   *
   * Granted on every tier today. Switching packaging on is setting it `false`
   * here AND in `plans.py`.
   */
  API_KEYS: "api_keys"
};
var ENTITLEMENT_NAMES = new Set(
  bundledPlans().flatMap((plan) => plan.entitlements.map((e) => e.name))
);
function planWithout(...withheld) {
  return {
    id: "fixture",
    display_name: "Fixture",
    entitlements: [...ENTITLEMENT_NAMES].map((name) => ({ name, enabled: !withheld.includes(name) })),
    quotas: []
  };
}
function isKnownEntitlement(name) {
  return ENTITLEMENT_NAMES.has(name);
}
var PLAN_GATED_SCOPES = {
  // Keyed off ENTITLEMENT rather than bare strings: a misspelled key here is a
  // name no plan can ever grant, so it would report its scopes as withheld for
  // every customer on every tier, and nothing would say so.
  [ENTITLEMENT.TRIGGERS]: ["triggers:read", "triggers:write", "watches:read", "watches:write"],
  [ENTITLEMENT.TENANCY_CREATION]: ["organizations:create", "tenants:create"],
  [ENTITLEMENT.CUSTOM_PIPELINES]: ["pipelines:write"]
};
function planWithheldEntitlements(granted) {
  if (granted === null) return [];
  return Object.keys(PLAN_GATED_SCOPES).filter((name) => !granted.has(name));
}
function staleClampedScopes(tokenScopes, granted) {
  const withheld = new Set(
    planWithheldEntitlements(granted).flatMap((name) => PLAN_GATED_SCOPES[name] ?? [])
  );
  if (withheld.size === 0) return [];
  return tokenScopes.filter((scope) => withheld.has(scope));
}
function overrideEnabled(raw) {
  if (typeof raw === "boolean") return raw;
  if (raw && typeof raw === "object" && "enabled" in raw) {
    const enabled = raw.enabled;
    if (typeof enabled === "boolean") return enabled;
  }
  return null;
}
function resolveEntitlements(plan, overrides = {}) {
  const resolved = plan.entitlements.map((e) => {
    const override = overrideEnabled(overrides[e.name]);
    return {
      name: e.name,
      enabled: override ?? e.enabled,
      overridden: override != null && override !== e.enabled
    };
  });
  for (const [name, raw] of Object.entries(overrides)) {
    if (plan.entitlements.some((e) => e.name === name)) continue;
    const override = overrideEnabled(raw);
    if (override == null) continue;
    resolved.push({ name, enabled: override, overridden: true });
  }
  return resolved;
}
var UNLIMITED = 0;
function overrideLimit(raw) {
  if (raw && typeof raw === "object" && "limit" in raw) {
    const value = raw.limit;
    if (typeof value === "number" && Number.isFinite(value)) return value;
  }
  return null;
}
function entitlementLimit(plan, overrides = {}, name) {
  if (!grantedEntitlements(plan, overrides).has(name)) return null;
  const override = overrideLimit(overrides[name]);
  const declared = plan.entitlements.find((e) => e.name === name)?.limit;
  const limit = override ?? declared ?? null;
  if (limit == null || !Number.isFinite(limit)) return null;
  return limit <= UNLIMITED ? null : limit;
}
function settingCount(planMetadata, subscriptionMetadata, key) {
  for (const bag of [subscriptionMetadata, planMetadata]) {
    const raw = bag?.[key];
    if (raw == null || typeof raw === "boolean") continue;
    const value = Number(raw);
    if (!Number.isFinite(value)) continue;
    return value <= UNLIMITED ? null : Math.trunc(value);
  }
  return null;
}
function grantedEntitlements(plan, overrides = {}) {
  return new Set(
    resolveEntitlements(plan, overrides).filter((e) => e.enabled).map((e) => e.name)
  );
}
function resolvePlanById(planId) {
  return bundledPlans().find((p) => p.id === planId) ?? PLAN_FREE;
}
function settingOptionLabel(def, option) {
  return def.option_labels?.[option] || option;
}
function settingValueLabel(def, value) {
  if (value === void 0 || value === null) {
    return def.when_unset ?? (def.kind === "choices" ? "All" : "Not set");
  }
  if (def.kind === "count") return Number(value) === UNLIMITED ? "Unlimited" : String(value);
  if (Array.isArray(value)) return value.length ? value.map((v) => settingOptionLabel(def, String(v))).join(", ") : "None";
  return settingOptionLabel(def, String(value));
}
function isBillable(e) {
  const raw = e.metadata?.[UsageMetadataKey.BILLABLE];
  return raw === void 0 ? !isCreditEvent(e) : raw === true;
}
function signedQuantity(e) {
  return isCreditEvent(e) ? -e.quantity : e.quantity;
}
function rollupUsage(events) {
  const byKind = {};
  const out = { billable: 0, telemetry: 0, byKind, credits: 0, events: 0 };
  for (const e of events) {
    const signed = signedQuantity(e);
    out.events += 1;
    byKind[e.kind] = (byKind[e.kind] ?? 0) + signed;
    if (isCreditEvent(e)) out.credits += 1;
    if (isBillable(e)) out.billable += signed;
    else out.telemetry += signed;
  }
  return out;
}
function usageForRun(events, pipelineRunId) {
  return rollupUsage(events.filter((e) => e.pipeline_run_id === pipelineRunId));
}
function attributionPath(e) {
  const rungs = [
    ["customer", e.customer_id],
    ["org", e.org_id],
    ["tenant", e.tenant_id],
    ["user", e.user_id]
  ];
  return rungs.filter(([, id]) => Boolean(id) && id !== "main").map(([rung, id]) => ({ rung, id }));
}
function groupUsageBy(events, rung) {
  const read = (e) => {
    switch (rung) {
      case "customer":
        return e.customer_id;
      case "org":
        return e.org_id;
      case "tenant":
        return e.tenant_id;
      case "user":
        return e.user_id;
      case "actor":
        return e.actor;
    }
  };
  const buckets = /* @__PURE__ */ new Map();
  for (const e of events) {
    const bucket = read(e) ?? "unattributed";
    const existing = buckets.get(bucket);
    if (existing) existing.push(e);
    else buckets.set(bucket, [e]);
  }
  return [...buckets.entries()].map(([key, rows]) => ({ key, rollup: rollupUsage(rows) })).sort((a, b) => b.rollup.billable - a.rollup.billable);
}
function usedForPolicy(events, policy, period, rollup, eventsComplete = true) {
  if (period.granularity !== policy.window.granularity) return null;
  const kindOnly = policy.subkind == null && !(policy.applies_to_tiers && policy.applies_to_tiers.length > 0);
  if (rollup?.byKind && kindOnly) return rollup.byKind[policy.kind] ?? 0;
  if (!eventsComplete) return null;
  let used = 0;
  for (const e of events) {
    if (policyMatches(policy, e)) used += signedQuantity(e);
  }
  return used;
}
function estimateRunCost(count, options = {}) {
  const kind = options.kind ?? UsageEventKind.PER_TX;
  const units = Number.isFinite(count) ? Math.max(0, Math.trunc(count)) : 0;
  return {
    units: isBillableKind(kind) ? units : 0,
    kind,
    estimated: true
  };
}

export { BILLABLE_USAGE_KINDS, ENTITLEMENT, ENTITLEMENT_NAMES, EnforcementMode, OveragePolicy, PLAN_ENTERPRISE, PLAN_FREE, PLAN_GATED_SCOPES, PLAN_PRO, QuotaStatus, UNLIMITED, USAGE_KIND_LABELS, USAGE_UNIT_TERMS, UsageEventKind, UsageMetadataKey, UsageUnit, attributionPath, bundledPlans, createUsageApi, entitlementLimit, estimateRunCost, evaluateQuota, formatUsagePeriod, getEntitlement, grantedEntitlements, groupUsageBy, hasEntitlement, isBillable, isBillableKind, isBlocking, isBoundedWindow, isCreditEvent, isKnownEntitlement, isReject, planWithheldEntitlements, planWithout, policyMatches, quotaUsedFraction, readExecutionUsage, resolveEntitlements, resolvePlanById, rollupUsage, settingCount, settingOptionLabel, settingValueLabel, signedQuantity, staleClampedScopes, usageForRun, usageKindLabel, usedForPolicy };
//# sourceMappingURL=chunk-SAIWDT26.js.map
//# sourceMappingURL=chunk-SAIWDT26.js.map